import {useState, useCallback, useMemo, useRef, useEffect} from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {AddButtonProps} from 'shells/card/card-widget/card-widget';
import {CardWidgetEmptyState} from 'shells/empty-states/card-widget-empty-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SearchBar} from 'shells/search-bar/search-bar';
import {SelectMenu} from 'shells/select-menu/select-menu';
import {Body} from 'shells/typography/body';
import {useDebounce} from 'shells/utils/hooks/use-debounce';

import type {
    AccountCreateInput,
    PeepMapCreateInput,
    AccountUpdateInput,
    WidgetFragment,
    PeepRelatedPeepSortType,
    GetRelatedAccounts_contact_accounts_edges as AccountEdges,
} from 'nutshell-graphql-types';

import {useCreatePeepMap} from '../../../graphql/hooks/use-create-peep-map';
import {useDeletePeepMap} from '../../../graphql/hooks/use-delete-peep-map';
import {useUpdatePeepMap} from '../../../graphql/hooks/use-update-peep-map';
import {
    getFilterString,
    getSortByOptions,
    getPeepEmptyStateProps,
    getIsEmpty,
} from '../../../shared/related-peeps-widget/helpers';
import {TabSearchFilterSection} from '../../../shared/related-peeps-widget/tab-search-filter-section';
import type {RelatedPeepsTab} from '../../../shared/related-peeps-widget/types';
import type {UpdateAccountRelationshipMapInput} from '../../../types';

import {CollapsibleCardWidget} from '../../../../core/card/collapsible-card-widget';
import {useGetSessionUser} from '../../../../core/use-get-session-user';
import {WidgetSeeAllFooter} from '../../../../core-entity/components/widget-see-all-footer';
import {useCreateAccountForCoreEntity} from '../../../../core-entity/graphql/hooks/use-create-account-for-core-entity';
import {CompanyRow} from '../../../../core-entity/rows/peep-rows/company-row';

import {contactRefetchForEvent} from '../../hooks/use-contact-update-refetch';

import {AccountForm} from './account-form';
import {useGetRelatedAccounts} from './graphql/hooks/use-get-related-accounts';
import {useUpdateRelatedAccount} from './graphql/hooks/use-update-related-account';
import {getToolbarOptions} from './helpers';

import styles from './related-accounts-widget.module.css';

type PinnedAccountEdge = Omit<AccountEdges, '__typename'>;

interface Props {
    widget: WidgetFragment;
    contactId: string;
}

export function RelatedAccountsWidget(props: Props) {
    const [selectedTab, setSelectedTab] = useState<RelatedPeepsTab>('current');
    const [error, setError] = useState<string | null | undefined>(null);
    const [isCreatingAccount, setIsCreatingAccount] = useState<boolean>(false);
    const [isEditingAccountId, setIsEditingAccountId] = useState<string | null>(null);
    const [showSearch, setShowSearch] = useState<boolean>(false);
    const [currentSearch, setCurrentSearch] = useState<string | null>(null);
    const [formerSearch, setFormerSearch] = useState<string | null>(null);
    const [showSortBy, setShowSortBy] = useState<boolean>(false);
    const [currentSortBy, setCurrentSortBy] = useState<PeepRelatedPeepSortType | null>(null);
    const [formerSortBy, setFormerSortBy] = useState<PeepRelatedPeepSortType | null>(null);
    const [pinnedAccounts, setPinnedAccounts] = useState<PinnedAccountEdge[]>([]);
    const [newlyAddedAccountId, setNewlyAddedAccountId] = useState<string | null>(null);
    const [localIsCurrentExpanded, setLocalIsCurrentExpanded] = useState<boolean>(false);
    const [localIsFormerExpanded, setLocalIsFormerExpanded] = useState<boolean>(false);

    const sortByButtonRef = useRef<HTMLButtonElement | null | undefined>(null);

    const debouncedCurrentSearch = useDebounce(currentSearch, 300);
    const debouncedFormerSearch = useDebounce(formerSearch, 300);

    const {user, isLoading: isLoadingUser} = useGetSessionUser();
    const {
        accounts: currentAccounts,
        totalCount: currentUnfilteredCount,
        isLoading: isLoadingCurrentAccounts,
        isLoadingMore: isLoadingMoreCurrentAccounts,
        isRefreshing: isRefreshingCurrentAccounts,
        isRefetching: isRefetchingCurrentAccounts,
        isExpanded: hookIsCurrentExpanded,
        hasNextPage: hasNextPageCurrentAccounts,
        fetchAllAccounts: fetchAllCurrentAccounts,
        refetch: refetchCurrentAccounts,
    } = useGetRelatedAccounts(props.contactId, false, false, debouncedCurrentSearch, currentSortBy);
    const {
        accounts: formerAccounts,
        totalCount: formerUnfilteredCount,
        isLoading: isLoadingFormerAccounts,
        isLoadingMore: isLoadingMoreFormerAccounts,
        isRefreshing: isRefreshingFormerAccounts,
        isRefetching: isRefetchingFormerAccounts,
        isExpanded: hookIsFormerExpanded,
        hasNextPage: hasNextPageFormerAccounts,
        fetchAllAccounts: fetchAllFormerAccounts,
        refetch: refetchFormerAccounts,
    } = useGetRelatedAccounts(props.contactId, true, false, debouncedFormerSearch, formerSortBy);

    const refetchAccounts = useCallback(() => {
        refetchCurrentAccounts();
        refetchFormerAccounts();
    }, [refetchCurrentAccounts, refetchFormerAccounts]);

    useEffect(() => {
        const handleContactUpdated = (event: Event) => {
            const customEvent = event as CustomEvent<{type: string}>;
            if (
                customEvent.detail.type === 'contactAccountsUpdated' ||
                customEvent.detail.type === 'contactEnrichmentUpdated'
            ) {
                refetchCurrentAccounts();
                refetchFormerAccounts();
            }
        };

        window.addEventListener('contactUpdated', handleContactUpdated);

        return () => window.removeEventListener('contactUpdated', handleContactUpdated);
    }, [refetchCurrentAccounts, refetchFormerAccounts]);

    const {deletePeepMap, isLoading: isLoadingDeletePeepMap} = useDeletePeepMap();
    const {updatePeepMap, isLoading: isLoadingUpdatePeepMap} = useUpdatePeepMap();
    const {createPeepMap, isLoading: isLoadingCreatePeepMap} = useCreatePeepMap();
    const {createAccountForCoreEntity, isLoading: isLoadingCreateAccount} =
        useCreateAccountForCoreEntity();
    const {updateRelatedAccount, isLoading: isLoadingUpdateRelatedAccount} =
        useUpdateRelatedAccount();

    const dispatchAccountsUpdated = useCallback(() => {
        contactRefetchForEvent('contactAccountsUpdated');
    }, []);

    const clearPinnedAccounts = useCallback(() => {
        setPinnedAccounts([]);
    }, []);

    const pinAccount = useCallback((account: PinnedAccountEdge) => {
        setPinnedAccounts((prevPinnedAccounts) => [
            account,
            ...prevPinnedAccounts.filter(
                (pinnedAccount) => pinnedAccount.node.id !== account.node.id
            ),
        ]);
    }, []);

    const removePinnedAccount = useCallback((accountId: string) => {
        setPinnedAccounts((prevPinnedAccounts) =>
            prevPinnedAccounts.filter((pinnedAccount) => pinnedAccount.node.id !== accountId)
        );
    }, []);

    const handleDeleteAccountMap = useCallback(
        (accountId: string) => {
            deletePeepMap({
                contact: props.contactId,
                account: accountId,
            })
                .then(() => {
                    removePinnedAccount(accountId);
                    dispatchAccountsUpdated();
                    refetchAccounts();
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [
            deletePeepMap,
            props.contactId,
            removePinnedAccount,
            dispatchAccountsUpdated,
            refetchAccounts,
        ]
    );

    const handleUpdateAccountMap = useCallback(
        (input: UpdateAccountRelationshipMapInput) => {
            updatePeepMap({
                contact: props.contactId,
                account: input.account,
                relationshipType: input?.relationshipType,
            })
                .then(() => {
                    removePinnedAccount(input.account);
                    dispatchAccountsUpdated();
                    refetchAccounts();
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [
            updatePeepMap,
            props.contactId,
            removePinnedAccount,
            dispatchAccountsUpdated,
            refetchAccounts,
        ]
    );

    const handleAddAccountMap = useCallback(
        (input: PeepMapCreateInput) => {
            createPeepMap(input)
                .then((result) => {
                    const account = result?.data?.peepMapCreate?.account;
                    if (account) {
                        pinAccount({
                            node: account,
                            isFormer: false,
                            relationshipNote: null,
                        });
                        setNewlyAddedAccountId(account.id);
                    }
                    dispatchAccountsUpdated();
                    refetchAccounts();
                    setIsCreatingAccount(false);
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [createPeepMap, pinAccount, dispatchAccountsUpdated, refetchAccounts]
    );

    const handleCreateAccount = useCallback(
        (input: AccountCreateInput) => {
            createAccountForCoreEntity(input)
                .then((result) => {
                    const account = result?.data?.accountCreate?.account;
                    if (account) {
                        pinAccount({
                            node: account,
                            isFormer: false,
                            relationshipNote: input.contacts?.[0]?.relationship ?? null,
                        });
                        setNewlyAddedAccountId(account.id);
                    }
                    dispatchAccountsUpdated();
                    refetchAccounts();
                    setIsCreatingAccount(false);
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [createAccountForCoreEntity, pinAccount, dispatchAccountsUpdated, refetchAccounts]
    );

    const handleUpdateRelatedAccount = useCallback(
        (input: AccountUpdateInput) => {
            updateRelatedAccount(input)
                .then((result) => {
                    const updatedAccount = result?.data?.accountUpdate?.account;
                    const updatedRelationshipNote =
                        input.contacts?.find((c) => c.id === props.contactId)?.relationship ?? null;

                    if (updatedAccount) {
                        setPinnedAccounts((prev) =>
                            prev.map((edge) =>
                                edge.node.id === updatedAccount.id
                                    ? {
                                          ...edge,
                                          node: {...edge.node, ...updatedAccount},
                                          relationshipNote: updatedRelationshipNote,
                                      }
                                    : edge
                            )
                        );
                    }
                    dispatchAccountsUpdated();
                    refetchAccounts();
                    setIsEditingAccountId(null);
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [updateRelatedAccount, dispatchAccountsUpdated, refetchAccounts, props.contactId]
    );

    const handleSeeAllCurrentAccounts = useCallback(() => {
        setLocalIsCurrentExpanded(true);
        fetchAllCurrentAccounts();
    }, [fetchAllCurrentAccounts]);

    const handleSeeAllFormerAccounts = useCallback(() => {
        setLocalIsFormerExpanded(true);
        fetchAllFormerAccounts();
    }, [fetchAllFormerAccounts]);

    const handleTabChange = useCallback(
        (newTab: RelatedPeepsTab) => {
            clearPinnedAccounts();
            setCurrentSortBy(null);
            setCurrentSearch(null);
            setFormerSortBy(null);
            setFormerSearch(null);
            setShowSearch(false);
            setSelectedTab(newTab);
            setIsCreatingAccount(false);
        },
        [clearPinnedAccounts]
    );

    const handleSortByChange = useCallback(
        (optionValue: PeepRelatedPeepSortType | null) => {
            clearPinnedAccounts();
            if (selectedTab === 'current') {
                setCurrentSortBy(optionValue);
            } else {
                setFormerSortBy(optionValue);
            }
            setShowSortBy(false);
        },
        [clearPinnedAccounts, selectedTab]
    );

    const handleSearchChange = useCallback(
        (value: string) => {
            clearPinnedAccounts();
            if (selectedTab === 'current') {
                setCurrentSearch(value);
                setLocalIsCurrentExpanded(false);
            } else {
                setFormerSearch(value);
                setLocalIsFormerExpanded(false);
            }
        },
        [clearPinnedAccounts, selectedTab]
    );

    const accountsToRender = useMemo(
        () => (selectedTab === 'current' ? currentAccounts : formerAccounts),
        [selectedTab, currentAccounts, formerAccounts]
    );

    const accountEdgesToRender = useMemo(() => {
        if (!accountsToRender || pinnedAccounts.length === 0) {
            return accountsToRender?.edges ?? [];
        }

        const backendEdgesById = new Map(
            accountsToRender.edges.map((account) => [account.node.id, account])
        );
        const pinnedAccountIds = new Set(pinnedAccounts.map((account) => account.node.id));

        // Get the backend edges for the pinned accounts if possible, otherwise use the pinned account data
        const mergedPinnedAccounts = pinnedAccounts.map(
            (account) => backendEdgesById.get(account.node.id) ?? account
        );
        const remainingAccounts = accountsToRender.edges.filter(
            (account) => !pinnedAccountIds.has(account.node.id)
        );

        return [...mergedPinnedAccounts, ...remainingAccounts];
    }, [accountsToRender, pinnedAccounts]);

    // Get a deduplicated list of all account IDs
    const allAccountsIds = useMemo(
        () =>
            Array.from(
                new Set([
                    ...(currentAccounts?.edges?.map((account) => account.node.id) ?? []),
                    ...(formerAccounts?.edges?.map((account) => account.node.id) ?? []),
                    ...pinnedAccounts.map((account) => account.node.id),
                ])
            ),
        [currentAccounts, formerAccounts, pinnedAccounts]
    );

    // Reset the newlyAddedAccountId once it has flashed once
    // so that it doesn't flash again if the account is updated while still pinned
    useEffect(() => {
        if (!newlyAddedAccountId) {
            return;
        }

        const isRendered = accountEdgesToRender.some(
            (account) => account.node.id === newlyAddedAccountId
        );

        if (!isRendered) {
            return;
        }

        const frame = window.requestAnimationFrame(() => {
            setNewlyAddedAccountId((currentId) =>
                currentId === newlyAddedAccountId ? null : currentId
            );
        });

        return () => window.cancelAnimationFrame(frame);
    }, [newlyAddedAccountId, accountEdgesToRender]);

    const isLoading =
        isLoadingUser ||
        (selectedTab === 'current'
            ? isLoadingCurrentAccounts && !showSearch
            : isLoadingFormerAccounts && !showSearch);
    const isReloading =
        isLoadingDeletePeepMap ||
        isLoadingUpdatePeepMap ||
        isRefetchingCurrentAccounts ||
        isRefetchingFormerAccounts ||
        isRefreshingCurrentAccounts ||
        isRefreshingFormerAccounts;
    const isLoadingMore =
        selectedTab === 'current' ? isLoadingMoreCurrentAccounts : isLoadingMoreFormerAccounts;

    const isEmpty = getIsEmpty(
        selectedTab,
        currentUnfilteredCount,
        formerUnfilteredCount,
        isCreatingAccount,
        isReloading,
        showSearch || showSortBy
    );

    const showHeaderSpinner = isReloading && !isEmpty;
    const isCurrentExpanded = hookIsCurrentExpanded || localIsCurrentExpanded;
    const isFormerExpanded = hookIsFormerExpanded || localIsFormerExpanded;

    const hasConnectedPhoneIntegration = Boolean(
        user &&
            (user.phoneIntegrations.some(
                (integration) => integration && integration.isEnabledForUser
            ) ||
                (user.clickToCallIntegration && user.clickToCallIntegration.isEnabled))
    );

    const emptyStateProps = getPeepEmptyStateProps('account', isEmpty);

    const addButtonProps: AddButtonProps = {
        variant: 'singular',
        onClick: () => {
            setIsCreatingAccount(true);
            setSelectedTab('current');
        },
        label: '+ Add company',
        isDisabled: isCreatingAccount,
    };

    return (
        <>
            <CollapsibleCardWidget
                widget={props.widget}
                isLoading={isLoading}
                addButtonProps={addButtonProps}
                emptyStateProps={emptyStateProps}
                errorProps={{
                    error: error,
                    setError: setError,
                }}
                hasBorder={true}
                showHeaderSpinner={showHeaderSpinner}
                tabProps={{
                    headerContent: (
                        <TabSearchFilterSection
                            selectedTab={selectedTab}
                            onTabChange={handleTabChange}
                            formerCount={formerUnfilteredCount}
                            currentCount={currentUnfilteredCount}
                            onSearchClicked={() => {
                                if (currentSearch !== null || formerSearch !== null) {
                                    clearPinnedAccounts();
                                }
                                setShowSearch((prevShowSearch) => !prevShowSearch);
                                setLocalIsCurrentExpanded(false);
                                setLocalIsFormerExpanded(false);
                                setCurrentSearch(null);
                                setFormerSearch(null);
                            }}
                            onFilterClicked={() => {
                                setShowSortBy((prevShowSortBy) => !prevShowSortBy);
                            }}
                            isSearching={showSearch}
                            hasSort={showSortBy || currentSortBy !== null || formerSortBy !== null}
                            getSortByButtonRef={(ref) => {
                                sortByButtonRef.current = ref;
                            }}
                        />
                    ),
                }}
            >
                <div className={styles['scroll-container']}>
                    {showSearch && (
                        <div className='pad-12 background-offwhite flex flex-dir-col gap-8'>
                            <SearchBar
                                placeholder='Search by name or location…'
                                value={
                                    selectedTab === 'current'
                                        ? currentSearch ?? ''
                                        : formerSearch ?? ''
                                }
                                onChange={handleSearchChange}
                                autoFocus={true}
                                hasBorder={true}
                            />
                            <Body size='small' color='light'>
                                {getFilterString(
                                    currentSortBy,
                                    formerSortBy,
                                    selectedTab,
                                    currentUnfilteredCount,
                                    formerUnfilteredCount,
                                    accountEdgesToRender.length ?? 0,
                                    'account'
                                )}
                            </Body>
                        </div>
                    )}
                    <div className='mt-8'>
                        {isCreatingAccount && (
                            <AccountForm
                                contactId={props.contactId}
                                onClose={() => setIsCreatingAccount(false)}
                                mutationProps={{
                                    variant: 'create',
                                    onCreate: handleCreateAccount,
                                    onAdd: handleAddAccountMap,
                                    alreadyAddedAccountIds: allAccountsIds,
                                }}
                                isSaving={isLoadingCreateAccount || isLoadingCreatePeepMap}
                            />
                        )}
                        {!accountEdgesToRender.length && (
                            <CardWidgetEmptyState {...getPeepEmptyStateProps('account', false)} />
                        )}
                        {accountEdgesToRender.map((account) => {
                            if (isEditingAccountId === account.node.id) {
                                return (
                                    <AccountForm
                                        contactId={props.contactId}
                                        mutationProps={{
                                            variant: 'update',
                                            onUpdate: handleUpdateRelatedAccount,
                                            temporaryAccount: {
                                                id: account.node.id,
                                                name: account.node.name,
                                                description: account.node.description,
                                                address:
                                                    account.node.postalAddresses?.primary?.location
                                                        ?.formattedAddress,
                                                jobTitle: account.relationshipNote,
                                            },
                                        }}
                                        onClose={() => setIsEditingAccountId(null)}
                                        isSaving={isLoadingUpdateRelatedAccount}
                                    />
                                );
                            }
                            return (
                                <CompanyRow
                                    key={account.node.id}
                                    accountId={account.node.id}
                                    name={account.node.name}
                                    avatarUrl={account.node.avatarUrl}
                                    accountUrl={account.node.htmlUrlPath}
                                    description={account.node.description}
                                    location={
                                        account.node.postalAddresses?.primary?.location
                                            ?.formattedAddress
                                    }
                                    role={account.relationshipNote}
                                    parentEntityName='Contacts'
                                    isDuplicate={account.node.hasDuplicates}
                                    isFormer={account.isFormer}
                                    removeAccount={handleDeleteAccountMap}
                                    setError={setError}
                                    phoneCallerType={user?.phoneCallerType}
                                    hasConnectedPhoneIntegration={hasConnectedPhoneIntegration}
                                    phone={account.node.phones?.primary?.value}
                                    prependToolbarOptions={getToolbarOptions(
                                        account.node.id,
                                        account.isFormer,
                                        handleUpdateAccountMap,
                                        setIsEditingAccountId
                                    )}
                                    successFlashKey={
                                        newlyAddedAccountId === account.node.id
                                            ? account.node.id
                                            : undefined
                                    }
                                />
                            );
                        })}
                        {isLoadingMore && <LoadingPage padding={8} />}
                    </div>
                </div>
                <WidgetSeeAllFooter
                    seeAllProps={{
                        isExpanded:
                            selectedTab === 'current' ? isCurrentExpanded : isFormerExpanded,
                        hasMore:
                            selectedTab === 'current'
                                ? hasNextPageCurrentAccounts
                                : hasNextPageFormerAccounts,
                        term: `${selectedTab} companies`,
                        onClick:
                            selectedTab === 'current'
                                ? handleSeeAllCurrentAccounts
                                : handleSeeAllFormerAccounts,
                    }}
                    listLink={`/companies?filter[contacts][data][0][data]=${props.contactId}&view=list`}
                    listLinkItemCount={
                        selectedTab === 'current' ? currentUnfilteredCount : formerUnfilteredCount
                    }
                />
            </CollapsibleCardWidget>
            {showSortBy && (
                <SelectMenu
                    anchor={sortByButtonRef.current}
                    onClose={() => setShowSortBy(false)}
                    options={getSortByOptions(
                        currentSortBy,
                        formerSortBy,
                        selectedTab,
                        handleSortByChange
                    )}
                    location='bottom-left'
                />
            )}
        </>
    );
}
